/**
 * ManageMembersModal — add / remove / hide members and set their role
 * (manager vs member) and engagement (full time / part time / contract) on a
 * project. Opened from the People sidebar tab.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { X, Trash2, Eye, EyeOff } from "lucide-react";
import { wsApi } from "../wsApi";
import { T, selectChevron } from "../../../components/em/ui";
import { MemberCombobox } from "../wsUi";

// Engagement is independent of role: a Manager can be part time, a Member can
// be on contract. Stored values are the DB enum; labels are what the UI shows.
const ENGAGEMENTS = [
  ["full_time", "Full Time"],
  ["part_time", "Part Time"],
  ["contract", "Contract"],
];

export default function ManageMembersModal({ project, users, onClose, onChanged }) {
  const members = project.members || [];
  const memberIds = new Set(members.map((m) => Number(m.user_id)));
  const [busy, setBusy] = useState(false);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); onChanged(); }
    catch { toast.error("Action failed"); }
    finally { setBusy(false); }
  };

  const toggleUser = (uid) => {
    const id = Number(uid);
    if (memberIds.has(id)) act(() => wsApi.removeProjectMember(project.id, id));
    else act(() => wsApi.addProjectMember(project.id, { user_id: id, role: "member" }));
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={head}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Manage people</h3>
          <button onClick={onClose} style={{ background: "none", border: 0, cursor: "pointer", display: "flex", padding: 4 }}><X size={18} /></button>
        </div>

        {/* Add people first — it's the combobox with a dropdown, so it needs
            open room below it. Putting it above the (potentially long,
            independently-scrolling) members list means the dropdown never
            has to fight that list for space or risk being clipped by it. */}
        <div style={{ fontSize: 12, fontWeight: 700, color: T.fg5, margin: "4px 0 8px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Add people
        </div>
        <div style={{ marginBottom: 20 }}>
          <MemberCombobox users={users.filter((u) => !memberIds.has(Number(u.id)))} selected={[]} onToggle={toggleUser}
            placeholder="Search people to add…" />
        </div>

        {/* Current members with role / hide / remove. Capped + internally
            scrollable so a long roster can't push the modal (and this
            section) to grow past the viewport. */}
        <div style={{ fontSize: 12, fontWeight: 700, color: T.fg5, margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          On this project ({members.length})
        </div>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", maxHeight: 260, overflowY: "auto" }}>
          {members.length === 0 && <div style={{ padding: 14, color: T.fg7, font: `400 12px ${T.font}` }}>No one yet — add people above.</div>}
          {members.map((m, i) => (
            <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderTop: i ? `1px solid ${T.divider}` : "none", opacity: m.hidden ? 0.55 : 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: `600 13px ${T.font}`, color: T.fg1, ...truncate }}>{m.full_name || m.username}</div>
                <div style={{ font: `400 11px ${T.font}`, color: T.fg7, ...truncate }}>{m.email}</div>
              </div>
              <select
                value={m.role || "member"}
                disabled={busy}
                onChange={(e) => act(() => wsApi.updateProjectMember(project.id, m.user_id, { role: e.target.value }))}
                style={{ ...rowSelect, width: 96 }}
              >
                <option value="manager">Manager</option>
                <option value="member">Member</option>
              </select>
              <select
                value={m.engagement || "full_time"}
                disabled={busy}
                title="Engagement"
                onChange={(e) => act(() => wsApi.updateProjectMember(project.id, m.user_id, { engagement: e.target.value }))}
                style={{ ...rowSelect, width: 104 }}
              >
                {ENGAGEMENTS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <button title={m.hidden ? "Show" : "Hide"} disabled={busy}
                onClick={() => act(() => wsApi.updateProjectMember(project.id, m.user_id, { hidden: m.hidden ? 0 : 1 }))}
                style={{ background: "none", border: 0, cursor: "pointer", color: T.fg5, display: "flex", padding: 4 }}>
                {m.hidden ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
              <button title="Remove" disabled={busy}
                onClick={() => act(() => wsApi.removeProjectMember(project.id, m.user_id))}
                style={{ background: "none", border: 0, cursor: "pointer", color: T.danger, display: "flex", padding: 4 }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
          <button onClick={onClose} style={{ background: T.gradient, color: "#fff", border: 0, borderRadius: 8, padding: "9px 18px", cursor: "pointer", font: `600 13px ${T.font}` }}>Done</button>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
// 520 rather than 460: the row carries two selects now, and at 460 the name /
// email column was squeezed to the point of truncating ordinary names.
const modal = { background: "var(--em-bg-card)", borderRadius: 14, padding: "20px 22px", width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.2)" };
const rowSelect = { flexShrink: 0, border: `1px solid ${T.border}`, borderRadius: 7, padding: "5px 8px", font: `500 12px ${T.font}`, background: "var(--em-bg-card)", color: "var(--em-fg1)", ...selectChevron, paddingRight: 22 };
const truncate = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const head = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 };
